import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { cn } from '@/shared/lib';
import type { SheetCloseProps, SheetProps, SheetTriggerProps } from '@/shared/ui/sheet/types';
import { SheetSideContext } from '@/shared/ui/sheet/hooks';
import { sheetStyles } from '@/shared/ui/sheet/styles';

/**
 * Root of the sheet surface.
 *
 * A sheet is a dialog anchored to an edge, so it is built on the same dialog
 * primitive. Reimplementing the modal, focus trap and focus restore for a
 * different border radius is a second implementation of behaviour that is
 * already correct — and the copy is the one that drifts.
 */
export function Sheet({
  children,
  modal = true,
  onOpenChange,
  open,
  defaultOpen,
  side = 'right',
}: SheetProps) {
  return (
    <SheetSideContext.Provider value={side}>
      <DialogPrimitive.Root
        modal={modal}
        onOpenChange={onOpenChange}
        open={open}
        defaultOpen={defaultOpen}
      >
        {children}
      </DialogPrimitive.Root>
    </SheetSideContext.Provider>
  );
}

/** The control that opens the sheet and receives focus back when it closes. */
export function SheetTrigger({ className, type = 'button', ...props }: SheetTriggerProps) {
  return (
    <DialogPrimitive.Trigger className={cn(sheetStyles.trigger, className)} type={type} {...props} />
  );
}

/**
 * Closes the sheet. Belongs inside the panel — touch screen readers need an
 * in-context escape while focus trapping is active.
 */
export function SheetClose({ className, type = 'button', ...props }: SheetCloseProps) {
  return (
    <DialogPrimitive.Close className={cn(sheetStyles.close, className)} type={type} {...props} />
  );
}